/**
 * Полоса плотности от ширины контейнера.
 *
 * ResizeObserver пишет `data-band` на тот же узел, который меряем.
 * Первый кадр — `clientWidth`, без вспышки room→fist: ноль ещё не кулак
 * (`bandOf`), а `useLayoutEffect` ставит атрибут до отрисовки браузером.
 *
 * Контекст нужен ситу и тестам: jsdom не считает @container.
 */

import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import { type Band, nextBand } from './band';

export const BandContext = createContext<Band>('room');

export function useFeedBand(): Band {
  return useContext(BandContext);
}

export function useBand(ref: RefObject<HTMLElement | null>): Band {
  const [band, setBand] = useState<Band>('room');
  const current = useRef<Band | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;

    const apply = (px: number): void => {
      const next = nextBand(px, current.current);
      if (current.current === next && el.dataset.band === next) return;
      current.current = next;
      el.dataset.band = next;
      setBand(next);
    };

    apply(el.clientWidth);

    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      apply(el.clientWidth);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return band;
}

/** Корень страницы с `data-band` и контекстом для сита. */
export function BandShell({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const band = useBand(ref);
  return (
    <BandContext.Provider value={band}>
      <div ref={ref} className={className}>
        {children}
      </div>
    </BandContext.Provider>
  );
}
